import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Input, Tab } from 'semantic-ui-react';
import { LIGHT_USER_ROLE_SLUG, SURVEY_STATUS_KEYS } from '~/Constants';
import Layout from '~/components/Layout';
import { El } from '~/components/Layout/style';
import TableComponent from '~/components/TableComponent';
import i18n from '~/components/i18n';
import { useAuth } from '~/routes/components/AuthProvider';
import { RouteTypes } from '~/routes/constants';
import { useDebounce } from 'use-lodash-debounce';
import PlanModal from './Modals/PlanModal';
import DeleteModal from './Modals/DeleteModal';
import { CoolFarmQuery } from '~/QueryHooks/CoolFarm';

const getDefaultTableColumnHeaders = () => [
  {
    name: i18n.t('ID'),
    key: 'uniqueId'
  },
  {
    name: i18n.t('Period under consideration'),
    key: 'period',
    type: 'climate-check-period'
  },
  {
    name: i18n.t('Title/Topic'),
    key: 'title'
  },
  {
    name: i18n.t('Start/end data collection'),
    key: 'start',
    type: 'startEndSurvey',
    sorted: 'desc'
  },
  {
    name: i18n.t('Farmer groups(n)'),
    key: 'farmerGroups',
    type: 'farmer-groups'
  },
  {
    name: i18n.t('Participants'),
    key: 'participants',
    type: 'survey-participants'
  },
  {
    name: i18n.t('Actions')
  }
];

const getActions = () => [
  /* {
    popupMessage: i18n.t('Show'),
    type: 'link',
    name: 'eye',
    size: 'large',
    link: RouteTypes.CO2_CLIMATE_SHOW,
    disableForLightUser: true
  },
  {
    popupMessage: i18n.t('Results'),
    type: 'link',
    name: 'clipboard list',
    size: 'large',
    link: RouteTypes.CO2_CLIMATE_RESULTS,
    display: [SURVEY_STATUS_KEYS.live, SURVEY_STATUS_KEYS.completed],
    disableForLightUser: true
  } */
  {
    popupMessage: i18n.t('Edit'),
    type: 'link',
    name: 'pencil',
    size: 'large',
    link: RouteTypes.COOL_FARM_EDIT,
    display: [SURVEY_STATUS_KEYS.defined],
    disableForLightUser: true
  },
  {
    popupMessage: i18n.t('Plan'),
    type: 'icon',
    name: 'check circle outline',
    size: 'large',
    actionType: 'plan',
    display: [SURVEY_STATUS_KEYS.defined],
    disableForLightUser: true
  },
  {
    popupMessage: i18n.t('Delete'),
    type: 'icon',
    name: 'trash',
    size: 'large',
    actionType: 'delete',
    display: [SURVEY_STATUS_KEYS.defined],
    disableForLightUser: true
  }
]

const CoolFarm: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { systemData: { logged: { roleSlug } } } = useAuth()
  const lightUserLogged = roleSlug === LIGHT_USER_ROLE_SLUG
  const actions = getActions()
  const [search, setSearch] = useState('')
  const debouncedSearchValue = useDebounce(search, 300)

  const [deleteData, setDeleteData] = useState(null)
  const [isExporting, setIsExporting] = useState(false)
  const [planData, setPlanData] = useState(null)
  const [tableColumnHeaders, setTableColumnHeaders] = useState(getDefaultTableColumnHeaders())
  const [activeTabIndex, setActiveTabIndex] = useState(0)
  const [dataFiltered, setDataFiltered] = useState({
    page: 0,
    rows: 30,
    search: '',
    status: 'actual',
    column: '',
    sort: ''
  })

  const {
    data: coolFarmOptions,
    isLoading: isBusyOptions,
    refetch: fetchOptions
  } = CoolFarmQuery.getCoolFarmOptions({
    ...dataFiltered,
    page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1
  })

  const {
    data: coolFarmData,
    isLoading: isBusyData,
    refetch: fetchFilteredData,
    isFetching: isFetchingData
  } = CoolFarmQuery.getCoolFarmFootprints({
    ...dataFiltered,
    page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1
  })

  const {
    data: coolFarmCount,
    isLoading: isBusyCount,
    refetch: fetchCount
  } = CoolFarmQuery.getCoolFarmCount({
    ...dataFiltered,
    page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1,
    search: debouncedSearchValue
  })

  useEffect(() => {
    setTableColumnHeaders(getDefaultTableColumnHeaders())
    fetchFilteredData()
    fetchOptions()
  }, [t])

  useEffect(() => {
    if (dataFiltered) {
      fetchFilteredData();
    }
  }, [dataFiltered]);

  useEffect(() => {
    setDataFiltered({ ...dataFiltered, search: debouncedSearchValue, page: 0 });
    fetchCount()
  }, [debouncedSearchValue]);

  function renderSearch() {
    return (
      <Input
        icon={{ name: 'search', circular: true, link: true }}
        placeholder={t('Search...')}
        value={search}
        onChange={(e, data) => setSearch(data.value)}
      />
    );
  }

  function handleClickAction(row, type) {
    switch (type) {
      case 'delete':
        return setDeleteData(row)
      case 'plan':
        return setPlanData(row)
      default:
        return
    }
  }

  function renderContent() {
    const actionsFiltered = lightUserLogged ? actions.filter(action => !action.disableForLightUser) : actions
    return <TableComponent
      actions={actionsFiltered}
      isFetchingData={isFetchingData}
      animated
      handleClickAction={handleClickAction}
      tableColumnHeaders={tableColumnHeaders}
      dataFiltered={dataFiltered}
      handleChangeDataFiltered={(data, tableColumnHeaders) => {
        setTableColumnHeaders(tableColumnHeaders)
        setDataFiltered(data)
      }}
      rows={rows}
      total={filtered}
    />
  }

  function handleTabChange(activeIndex) {
    if (!isFetchingData) {
      const status = coolFarmOptions?.statuses?.find((_, indexOption) => indexOption === activeIndex);
      setDataFiltered({
        ...dataFiltered,
        status: status.key,
        page: 0
      });
      setActiveTabIndex(activeIndex)
    }
  }

  function renderPanes() {
    const panes = coolFarmOptions?.statuses?.map((option) => {
      const countIndex = coolFarmCount.findIndex((count) => count.status == option.key);
      return {
        menuItem: `${option.label}  (${coolFarmCount[countIndex]?.count || 0})`,
        render: () => renderContent()
      };
    });

    return (
      <Tab
        menu={{ secondary: true, pointing: true }}
        activeIndex={activeTabIndex}
        panes={panes}
        onTabChange={(_, data) => handleTabChange(data.activeIndex)}
      />
    );
  }

  function renderActions() {
    return <>
      <El.PageHeaderActionButton
        size='large'
        onClick={() => navigate(RouteTypes.COOL_FARM_CREATE)}
      >
        {t('Create Cool Farm data collection')}
      </El.PageHeaderActionButton>
    </>
  }

  function renderPlanModal() {
    return <PlanModal
      planData={planData}
      handleClose={(submit) => {
        setPlanData(null)
        if (submit) {
          fetchFilteredData()
          fetchCount()
        }
      }
      }
    />
  }

  function renderDeleteModal() {
    return <DeleteModal
      deleteData={deleteData}
      handleClose={(submit) => {
        setDeleteData(null)
        if (submit) {
          fetchFilteredData()
          fetchCount()
        }
      }
      }
    />
  }

  const { filtered, rows } = coolFarmData ? coolFarmData : { filtered: 0, rows: [] };
  const isLoading = isBusyOptions || isBusyData || isBusyCount

  return (
    <Layout
      isBusy={isLoading}
      title={t('Cool Farm')}
      isCo2Loader={true}
      filters={renderSearch()}
      segmented
      actions={!lightUserLogged && renderActions()}
    >
      {renderDeleteModal()}
      {renderPlanModal()}
      {coolFarmData && coolFarmCount && coolFarmOptions && renderPanes()}
    </Layout>
  );
};

export default CoolFarm;